<template>
  <div v-if="show" class="webhook-success-popup-overlay" @click="close">
    <div class="webhook-success-popup" @click.stop>
      <div class="popup-header">
        <div class="success-icon">
          <i class="fas fa-check-circle"></i>
        </div>
        <h3>Webhook Test Successful!</h3>
        <button @click="close" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="popup-content">
        <div class="success-message">
          <p><strong>Status:</strong> {{ responseData.status || 200 }}</p>
          <p><strong>Response Time:</strong> {{ responseData.responseTime || 0 }}ms</p>
          <p v-if="displayEndpoint"><strong>Endpoint:</strong> {{ displayEndpoint }}</p>
          <p v-if="responseData.webhook_url"><strong>Webhook URL:</strong> {{ responseData.webhook_url }}</p>
          <p><strong>Event:</strong> {{ responseData.eventKey }}</p>
        </div>
        
        <div class="response-details" v-if="responseData.response">
          <h4>Response Details:</h4>
          <div class="response-info">
            <p><strong>Status Code:</strong> {{ responseData.response.status }}</p>
            <p><strong>Status Text:</strong> {{ responseData.response.statusText }}</p>
            <div v-if="responseData.response.headers" class="headers-section">
              <h5>Response Headers:</h5>
              <div class="headers-list">
                <div v-for="(value, key) in responseData.response.headers" :key="key" class="header-item">
                  <span class="header-key">{{ key }}:</span>
                  <span class="header-value">{{ value }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="popup-actions">
          <button @click="close" class="btn-primary">
            <i class="fas fa-check"></i>
            Got it!
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  responseData: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['close'])

const close = () => {
  emit('close')
}

// Filter out internal endpoints and show only client endpoints
const displayEndpoint = computed(() => {
  const endpoint = props.responseData.endpointUrl
  if (!endpoint) return null
  
  // Don't show internal endpoints
  const internalEndpoints = [
    '/api/webhook-test',
    '/api/webhooks/test',
    '/api/webhooks/test/v2'
  ]
  
  if (internalEndpoints.includes(endpoint)) {
    return null
  }
  
  return endpoint
})

// Auto-close after 8 seconds
watch(() => props.show, (newValue) => {
  if (newValue) {
    setTimeout(() => {
      close()
    }, 8000)
  }
})
</script>

<style scoped>
.webhook-success-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease-out;
}

.webhook-success-popup {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  animation: slideIn 0.3s ease-out;
}

.popup-header {
  display: flex;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
}

.success-icon {
  color: #10b981;
  font-size: 24px;
  margin-right: 12px;
  animation: bounce 0.6s ease-out;
}

.popup-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.popup-content {
  padding: 24px;
}

.success-message {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.success-message p {
  margin: 4px 0;
  color: #166534;
  font-size: 14px;
}

.response-details {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.response-details h4 {
  margin: 0 0 12px 0;
  color: #1e293b;
  font-size: 16px;
  font-weight: 600;
}

.response-info p {
  margin: 6px 0;
  color: #475569;
  font-size: 14px;
}

.headers-section {
  margin-top: 12px;
}

.headers-section h5 {
  margin: 0 0 8px 0;
  color: #334155;
  font-size: 14px;
  font-weight: 600;
}

.headers-list {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px;
  max-height: 120px;
  overflow-y: auto;
}

.header-item {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 12px;
}

.header-item:last-child {
  border-bottom: none;
}

.header-key {
  color: #64748b;
  font-weight: 500;
  min-width: 120px;
}

.header-value {
  color: #1e293b;
  word-break: break-all;
}

.popup-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-primary:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from { 
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}
</style>
